/** Supported text conversion formats. */
export type EncodingFormat = "base64" | "url" | "hex";

/** Result of a validated browser-only operation. */
export type ToolResult<T> = { success: true; value: T } | { success: false; error: string };

/** IPv4 subnet information, including point-to-point and single-host ranges. */
export type CidrResult = {
	cidr: string; ip: string; network: string; broadcast: string; mask: string; prefix: number;
	firstHost: string; lastHost: string; totalAddresses: number; usableHosts: number;
};



const getIpv4Number = (address: string) : number | null => {
	if(!/^(0|[1-9]\d{0,2})(\.(0|[1-9]\d{0,2})){3}$/.test(address)) return null;
	const octets = address.split(".").map(Number);
	if(octets.some((octet) => octet > 255)) return null;
	return octets.reduce((total, octet) => (total * 256) + octet, 0);
};

const getIpv4Address = (address: number) : string => [ 24, 16, 8, 0 ].map((shift) => (address >>> shift) & 255).join(".");



/**
 * Converts text using native browser APIs and rejects invalid UTF-8 output.
 * @param input Text or encoded bytes to convert.
 * @param format Encoding to apply or read.
 * @param operation Whether to encode text or decode bytes.
 * @returns Converted text or a validation message.
 */
export const convertText = (input: string, format: EncodingFormat, operation: "encode" | "decode") : ToolResult<string> => {
	try {
		if(format === "url") return { success: true, value: operation === "encode" ? encodeURIComponent(input) : decodeURIComponent(input) };
		if(operation === "encode") {
			// TextEncoder replaces lone surrogates; reject them to avoid changing the user's text.
			encodeURIComponent(input);
			const bytes = new TextEncoder().encode(input);
			if(format === "hex") return { success: true, value: Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("") };
			return { success: true, value: btoa(Array.from(bytes, (byte) => String.fromCharCode(byte)).join("")) };
		}
		let bytes = new Uint8Array();
		if(format === "hex") {
			if(!/^(?:[\da-fA-F]{2})*$/.test(input)) return { success: false, error: "Enter complete hexadecimal byte pairs (0–9, A–F), without separators." };
			bytes = Uint8Array.from(input.match(/.{2}/g) || [], (byte) => parseInt(byte, 16));
		} else {
			if(!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(input)) return { success: false, error: "Enter valid Base64 with the required padding (=)." };
			const binary = atob(input);
			if(btoa(binary) !== input) return { success: false, error: "Enter valid Base64 with canonical padding bits." };
			bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
		}
		return { success: true, value: new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes) };
	} catch {
		return { success: false, error: format === "url" ? "Enter valid URL-encoded UTF-8 text. Check percent escapes and Unicode characters." : "The value must represent valid UTF-8 text. Binary files and invalid Unicode cannot be converted here." };
	}
};

/**
 * Calculates an IPv4 subnet without sending or storing the input.
 * @param input IPv4 address followed by a prefix from 0 through 32.
 * @returns Subnet information or a validation message.
 */
export const calculateCidr = (input: string) : ToolResult<CidrResult> => {
	const parts = input.trim().split("/");
	const ip = getIpv4Number(parts[0]);
	if(parts.length !== 2 || ip === null || !/^(0|[1-9]\d?)$/.test(parts[1]) || Number(parts[1]) > 32) return { success: false, error: "Enter an IPv4 address and prefix from 0 to 32, such as 192.168.1.0/24." };
	const prefix = Number(parts[1]);
	const totalAddresses = 2 ** (32 - prefix);
	const network = Math.floor(ip / totalAddresses) * totalAddresses;
	const broadcast = network + totalAddresses - 1;
	const usableHosts = prefix >= 31 ? totalAddresses : totalAddresses - 2;
	return { success: true, value: {
		cidr: `${ getIpv4Address(network) }/${ prefix }`, ip: getIpv4Address(ip), network: getIpv4Address(network), broadcast: getIpv4Address(broadcast),
		mask: getIpv4Address((2 ** 32) - totalAddresses), prefix,
		firstHost: getIpv4Address(prefix >= 31 ? network : network + 1), lastHost: getIpv4Address(prefix >= 31 ? broadcast : broadcast - 1), totalAddresses, usableHosts
	} };
};

